import { useState } from 'react';
import Layout from '@/components/Layout';
import { useWarehouses } from '@/hooks/useWarehouses';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Building2,
  Plus,
  Search,
  MapPin,
  Phone,
  Package,
  Edit,
  Trash2,
  Building,
  Users,
  Activity,
  ToggleLeft,
  ToggleRight,
  Eye,
  Filter,
  X,
  Globe,
  Truck,
  FileText,
  LayoutGrid,
  List
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { countries, getStatesForCountry, getCitiesForState } from '@/data/countries';

interface Warehouse {
  id: string;
  name: string;
  code: string;
  location: string;
  address: string;
  city: string;
  state: string;
  country: string;
  phone: string;
  manager: string;
  capacity: number;
  currentStock: number;
  status: 'Active' | 'Inactive' | 'Maintenance';
  type: 'Main' | 'Branch' | 'Distribution';
  createdAt: string;
}

export default function Warehouses() {
  const { warehouses: apiWarehouses, isLoading, createWarehouse, updateWarehouse, deleteWarehouse } = useWarehouses();

  const warehouses = apiWarehouses || [];

  const [searchTerm, setSearchTerm] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isTripsDialogOpen, setIsTripsDialogOpen] = useState(false);
  const [isShipmentsDialogOpen, setIsShipmentsDialogOpen] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState<Warehouse | null>(null);
  const [selectedWarehouseShipments, setSelectedWarehouseShipments] = useState<any[]>([]);
  const [selectedWarehouseName, setSelectedWarehouseName] = useState('');
  const [selectedTrip, setSelectedTrip] = useState<any>(null);
  const [newWarehouse, setNewWarehouse] = useState({
    name: '',
    location: '',
    address: '',
    city: '',
    state: '',
    country: '',
    phone: '',
    manager: '',
    capacity: '',
    type: '',
    // Warehouse user creation fields
    createWarehouseUser: false,
    warehouseUsername: '',
    warehousePassword: ''
  });
  
  // Cascading dropdowns state for warehouse location
  const [warehouseStates, setWarehouseStates] = useState<any[]>([]);
  const [warehouseCities, setWarehouseCities] = useState<any[]>([]);

  // Filter states
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [countryFilter, setCountryFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const generateWarehouseCode = () => {
    const existingCodes = warehouses.map(w => parseInt(w.code.split('-')[1]));
    const nextCode = Math.max(...existingCodes, 0) + 1;
    return `WH-${nextCode.toString().padStart(3, '0')}`;
  };

  // Get unique values for filters
  const uniqueCountries = Array.from(new Set(warehouses.map(w => w.country))).filter(Boolean);
  const uniqueTypes = ['Main', 'Branch', 'Distribution'];

  // Apply all filters
  const filteredWarehouses = warehouses.filter(warehouse => {
    const searchMatch =
      warehouse.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      warehouse.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      warehouse.location.toLowerCase().includes(searchTerm.toLowerCase());

    const statusMatch = statusFilter === 'all' || warehouse.status === statusFilter;
    const typeMatch = typeFilter === 'all' || warehouse.type === typeFilter;
    const countryMatch = countryFilter === 'all' || warehouse.country === countryFilter;

    return searchMatch && statusMatch && typeMatch && countryMatch;
  }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  // Reset all filters
  const resetFilters = () => {
    setStatusFilter('all');
    setTypeFilter('all');
    setCountryFilter('all');
    setSearchTerm('');
  };

  // Get active filter count
  const activeFilterCount = [
    statusFilter !== 'all' ? 1 : 0,
    typeFilter !== 'all' ? 1 : 0,
    countryFilter !== 'all' ? 1 : 0,
    searchTerm.trim() !== '' ? 1 : 0
  ].reduce((sum, count) => sum + count, 0);

  // Edit warehouse function
  const handleEditWarehouse = () => {
    if (!editingWarehouse) return;

    // Use the updateWarehouse mutation from the hook
    updateWarehouse({
      code: editingWarehouse.code,
      name: editingWarehouse.name,
      location: editingWarehouse.location,
      address: editingWarehouse.address,
      city: editingWarehouse.city,
      state: editingWarehouse.state,
      country: editingWarehouse.country,
      phone: editingWarehouse.phone,
      manager: editingWarehouse.manager,
      capacity: Number(editingWarehouse.capacity) || 0,
      currentStock: Number(editingWarehouse.currentStock) || 0,
      type: editingWarehouse.type,
      status: editingWarehouse.status,
    });

    setEditingWarehouse(null);
    setIsEditDialogOpen(false);
  };

  // Delete warehouse function
  const handleDeleteWarehouse = (warehouseCode: string) => {
    deleteWarehouse(warehouseCode);
  };

  // Toggle warehouse status
  const handleToggleStatus = (warehouseCode: string) => {
    const warehouse = warehouses.find(w => w.code === warehouseCode);
    if (!warehouse) return;

    const newStatus = warehouse.status === 'Active' ? 'Maintenance' :
                      warehouse.status === 'Maintenance' ? 'Inactive' : 'Active';

    updateWarehouse({
      code: warehouse.code,
      status: newStatus
    });
  };

  // View warehouse trips
  const handleViewTrips = (warehouse: Warehouse) => {
    // Get real trips from localStorage that originate from this warehouse
    const savedTrips = localStorage.getItem('shiphub-trips');
    const warehouseTrips = savedTrips ? JSON.parse(savedTrips) : [];

    // Filter trips that originate from OR go to this warehouse
    const filteredTrips = warehouseTrips.filter((trip: any) =>
      trip.originWarehouse === warehouse.name ||
      trip.origin === warehouse.name ||
      trip.destinationWarehouse === warehouse.name ||
      trip.destination === warehouse.name
    );

    // Format trips for display with proper null checks
    const formattedTrips = filteredTrips.map((trip: any, index: number) => {
      const isOrigin = trip.originWarehouse === warehouse.name || trip.origin === warehouse.name;
      const isDestination = trip.destinationWarehouse === warehouse.name || trip.destination === warehouse.name;

      return {
        tripNumber: trip.name || 'Unnamed Trip',
        destination: trip.destinationWarehouse || trip.destination || 'Unknown',
        origin: trip.originWarehouse || trip.origin || 'Unknown',
        driver: trip.driver || 'Unassigned',
        vehicle: trip.vehicle || 'Unassigned',
        status: trip.status || 'Unknown',
        shipments: Array.isArray(trip.shipments) ? trip.shipments.length : 0,
        totalWeight: typeof trip.totalWeight === 'number' ? trip.totalWeight : 0,
        totalVolume: typeof trip.totalVolume === 'number' ? trip.totalVolume : 0,
        totalPackages: typeof trip.totalPackages === 'number' ? trip.totalPackages : 0,
        departureDate: trip.departureDate || '',
        estimatedArrival: trip.estimatedArrival || '',
        date: trip.departureDate || '',
        direction: isOrigin && isDestination ? 'Internal' : isOrigin ? 'Outgoing' : 'Incoming'
      };
    });

    setSelectedWarehouseShipments(formattedTrips);
    setSelectedWarehouseName(warehouse.name);
    setIsTripsDialogOpen(true);
  };

  // View shipments for a specific trip
  const handleViewTripShipments = (trip: any) => {
    // Get real shipments for this trip from localStorage
    const savedShipments = localStorage.getItem('shiphub-shipments');
    const allShipments = savedShipments ? JSON.parse(savedShipments) : [];

    // Filter shipments that belong to this trip
    const tripShipments = allShipments.filter((shipment: any) =>
      shipment && shipment.tripName === trip.tripNumber
    );

    setSelectedTrip(trip);
    setSelectedWarehouseShipments(tripShipments);
    setIsTripsDialogOpen(false);
    setIsShipmentsDialogOpen(true);
  };

  // Download warehouse info
  const handleDownloadPDF = (warehouse: Warehouse) => {
    const pdfContent = `
<!DOCTYPE html>
<html>
<head>
    <title>Warehouse Information - ${warehouse.name}</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
        .header { color: #2563eb; border-bottom: 2px solid #2563eb; padding-bottom: 10px; margin-bottom: 20px; }
        .section { margin: 20px 0; }
        .label { font-weight: bold; color: #374151; }
        .value { margin-bottom: 5px; }
        .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #6b7280; font-size: 12px; }
        .status-active { color: #16a34a; }
        .status-inactive { color: #dc2626; }
        .status-maintenance { color: #ca8a04; }
    </style>
</head>
<body>
    <div class="header">
        <h1>Warehouse Information</h1>
    </div>

    <div class="section">
        <div class="label">Warehouse Code:</div>
        <div class="value">${warehouse.code}</div>

        <div class="label">Name:</div>
        <div class="value">${warehouse.name}</div>

        <div class="label">Type:</div>
        <div class="value">${warehouse.type}</div>

        <div class="label">Location:</div>
        <div class="value">${warehouse.location}</div>

        <div class="label">Address:</div>
        <div class="value">${warehouse.address}, ${warehouse.city}, ${warehouse.state} ${warehouse.country}</div>

        <div class="label">Phone:</div>
        <div class="value">${warehouse.phone}</div>

        <div class="label">Manager:</div>
        <div class="value">${warehouse.manager}</div>

        <div class="label">Status:</div>
        <div class="value status-${warehouse.status.toLowerCase()}">${warehouse.status}</div>

        <div class="label">Capacity:</div>
        <div class="value">${warehouse.capacity.toLocaleString()} units</div>

        <div class="label">Current Stock:</div>
        <div class="value">${warehouse.currentStock.toLocaleString()} units</div>

        <div class="label">Utilization:</div>
        <div class="value">${Math.round((warehouse.currentStock / warehouse.capacity) * 100)}%</div>
    </div>

    <div class="footer">
        Generated on ${new Date().toLocaleDateString()} | SourceMyGoods - Shipping Management System
    </div>
</body>
</html>
    `;

    const blob = new Blob([pdfContent], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `warehouse-${warehouse.code}-${warehouse.name.replace(/\s+/g, '-')}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Print warehouse info
  const handlePrint = (warehouse: Warehouse) => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Warehouse Information - ${warehouse.name}</title>
        <style>
          @page {
            size: A4;
            margin: 15mm;
          }
          
          @media print {
            body { 
              padding: 0;
              margin: 0;
            }
          }
        </style>
      </head>
      <body>
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <h1 style="color: #2563eb; border-bottom: 2px solid #2563eb; padding-bottom: 10px;">Warehouse Information</h1>
        <div style="margin: 20px 0;">
          <p><strong>Warehouse Code:</strong> ${warehouse.code}</p>
          <p><strong>Name:</strong> ${warehouse.name}</p>
          <p><strong>Type:</strong> ${warehouse.type}</p>
          <p><strong>Location:</strong> ${warehouse.location}</p>
          <p><strong>Address:</strong> ${warehouse.address}, ${warehouse.city}, ${warehouse.state} ${warehouse.country}</p>
          <p><strong>Phone:</strong> ${warehouse.phone}</p>
          <p><strong>Manager:</strong> ${warehouse.manager}</p>
          <p><strong>Status:</strong> <span style="color: ${warehouse.status === 'Active' ? '#16a34a' : warehouse.status === 'Maintenance' ? '#ca8a04' : '#dc2626'}">${warehouse.status}</span></p>
          <p><strong>Capacity:</strong> ${warehouse.capacity.toLocaleString()} units</p>
          <p><strong>Current Stock:</strong> ${warehouse.currentStock.toLocaleString()} units</p>
          <p><strong>Utilization:</strong> ${Math.round((warehouse.currentStock / warehouse.capacity) * 100)}%</p>
        </div>
        <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #6b7280; font-size: 12px;">
          Generated on ${new Date().toLocaleDateString()} | SourceMyGoods - Shipping Management System
        </div>
      </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const handleAddWarehouse = () => {
    // Generate warehouse code from name
    const code = newWarehouse.name.toUpperCase().replace(/\s+/g, '_').substring(0, 10);

    createWarehouse({
      code: code,
      name: newWarehouse.name,
      type: newWarehouse.type,
      location: newWarehouse.location,
      address: newWarehouse.address,
      city: newWarehouse.city,
      state: newWarehouse.state,
      country: newWarehouse.country,
      phone: newWarehouse.phone,
      manager: newWarehouse.manager,
      capacity: parseInt(newWarehouse.capacity) || 0,
      current_stock: 0,
      status: 'Active'
    });

    // TODO: Create warehouse user if requested - needs separate API call
    // For now, warehouse users must be created manually via Users page

    setNewWarehouse({
      name: '',
      location: '',
      address: '',
      city: '',
      state: '',
      country: '',
      phone: '',
      manager: '',
      capacity: '',
      type: '',
      createWarehouseUser: false,
      warehouseUsername: '',
      warehousePassword: ''
    });
    setIsAddDialogOpen(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800';
      case 'Inactive': return 'bg-red-100 text-red-800';
      case 'Maintenance': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Main': return 'bg-blue-100 text-blue-800';
      case 'Branch': return 'bg-purple-100 text-purple-800';
      case 'Distribution': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCapacityPercentage = (current: number, capacity: number) => {
    if (!capacity || capacity === 0) return 0;
    return Math.round((current / capacity) * 100);
  };

  const getCapacityColor = (percentage: number) => {
    if (percentage >= 90) return 'text-red-600';
    if (percentage >= 70) return 'text-yellow-600';
    return 'text-green-600';
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Warehouses</h1>
            <p className="text-gray-600">Manage multiple warehouses across different locations</p>
          </div>

          <div className="flex space-x-2">
            {/* View Toggle */}
            <div className="flex border rounded-md">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="rounded-r-none"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="rounded-l-none"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-1"
            >
              <Filter className="h-4 w-4" />
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>

            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Warehouse
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Add New Warehouse</DialogTitle>
                <DialogDescription>
                  Create a new warehouse location for inventory management
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Warehouse Name</Label>
                    <Input
                      id="name"
                      value={newWarehouse.name}
                      onChange={(e) => setNewWarehouse({...newWarehouse, name: e.target.value})}
                      placeholder="Enter warehouse name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      value={newWarehouse.location}
                      onChange={(e) => setNewWarehouse({...newWarehouse, location: e.target.value})}
                      placeholder="City/Region"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={newWarehouse.address}
                    onChange={(e) => setNewWarehouse({...newWarehouse, address: e.target.value})}
                    placeholder="Street address"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Select
                    value={newWarehouse.country}
                    onValueChange={(value) => {
                      const states = getStatesForCountry(value);
                      setWarehouseStates(states);
                      setWarehouseCities([]);
                      setNewWarehouse({...newWarehouse, country: value, state: '', city: ''});
                    }}
                  >
                    <SelectTrigger id="country">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((country) => (
                        <SelectItem key={country.code} value={country.name}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="state">State/Province</Label>
                    <Select
                      value={newWarehouse.state}
                      onValueChange={(value) => {
                        const cities = getCitiesForState(newWarehouse.country, value);
                        setWarehouseCities(cities);
                        setNewWarehouse({...newWarehouse, state: value, city: ''});
                      }}
                      disabled={!newWarehouse.country}
                    >
                      <SelectTrigger id="state">
                        <SelectValue placeholder={newWarehouse.country ? "Select state" : "Select country first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {warehouseStates.map((state, index) => (
                          <SelectItem key={index} value={state.name}>
                            {state.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Select
                      value={newWarehouse.city}
                      onValueChange={(value) => setNewWarehouse({...newWarehouse, city: value})}
                      disabled={!newWarehouse.state}
                    >
                      <SelectTrigger id="city">
                        <SelectValue placeholder={newWarehouse.state ? "Select city" : "Select state first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {warehouseCities.map((city, index) => (
                          <SelectItem key={index} value={city.name}>
                            {city.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={newWarehouse.phone}
                      onChange={(e) => setNewWarehouse({...newWarehouse, phone: e.target.value})}
                      placeholder="Phone number"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="manager">Manager</Label>
                    <Input
                      id="manager"
                      value={newWarehouse.manager}
                      onChange={(e) => setNewWarehouse({...newWarehouse, manager: e.target.value})}
                      placeholder="Manager name"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="capacity">Capacity (units)</Label>
                    <Input
                      id="capacity"
                      type="number"
                      value={newWarehouse.capacity}
                      onChange={(e) => setNewWarehouse({...newWarehouse, capacity: e.target.value})}
                      placeholder="Storage capacity"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="type">Type</Label>
                    <Select value={newWarehouse.type} onValueChange={(value) => setNewWarehouse({...newWarehouse, type: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select warehouse type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Main">Main</SelectItem>
                        <SelectItem value="Branch">Branch</SelectItem>
                        <SelectItem value="Distribution">Distribution</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Warehouse User Creation Section */}
                <div className="border-t pt-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <input
                      type="checkbox"
                      id="create-warehouse-user"
                      checked={newWarehouse.createWarehouseUser}
                      onChange={(e) => setNewWarehouse({...newWarehouse, createWarehouseUser: e.target.checked})}
                      className="rounded border-gray-300"
                    />
                    <Label htmlFor="create-warehouse-user" className="text-sm font-medium">
                      Create warehouse manager user account
                    </Label>
                  </div>

                  {newWarehouse.createWarehouseUser && (
                    <div className="grid grid-cols-2 gap-4 mt-4 p-4 bg-gray-50 rounded-lg">
                      <div className="space-y-2">
                        <Label htmlFor="warehouse-username">Username</Label>
                        <Input
                          id="warehouse-username"
                          value={newWarehouse.warehouseUsername}
                          onChange={(e) => setNewWarehouse({...newWarehouse, warehouseUsername: e.target.value})}
                          placeholder="Enter username for warehouse manager"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="warehouse-password">Password</Label>
                        <Input
                          id="warehouse-password"
                          type="password"
                          value={newWarehouse.warehousePassword}
                          onChange={(e) => setNewWarehouse({...newWarehouse, warehousePassword: e.target.value})}
                          placeholder="Enter password for warehouse manager"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleAddWarehouse}>Add Warehouse</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search warehouses by name, code, or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Filters Section */}
        {showFilters && (
          <Card className="mt-4">
            <CardContent className="pt-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Filters</h3>
                <div className="flex space-x-2">
                  <Button variant="outline" size="sm" onClick={resetFilters}>
                    <X className="h-4 w-4 mr-1" />
                    Clear All
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowFilters(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="status-filter">Status</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger id="status-filter">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Maintenance">Maintenance</SelectItem>
                      <SelectItem value="Inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="type-filter">Type</Label>
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger id="type-filter">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {uniqueTypes.map(type => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country-filter">Country</Label>
                  <Select value={countryFilter} onValueChange={setCountryFilter}>
                    <SelectTrigger id="country-filter">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Countries</SelectItem>
                      {uniqueCountries.map(country => (
                        <SelectItem key={country} value={country}>
                          {country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Active Filters Display */}
              {activeFilterCount > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="text-sm font-medium mb-2">Active Filters:</h4>
                  <div className="flex flex-wrap gap-2">
                    {statusFilter !== 'all' && (
                      <Badge variant="secondary" className="flex items-center space-x-1">
                        <Activity className="h-3 w-3" />
                        Status: {statusFilter}
                      </Badge>
                    )}
                    {typeFilter !== 'all' && (
                      <Badge variant="secondary" className="flex items-center space-x-1">
                        <Building2 className="h-3 w-3" />
                        Type: {typeFilter}
                      </Badge>
                    )}
                    {countryFilter !== 'all' && (
                      <Badge variant="secondary" className="flex items-center space-x-1">
                        <Globe className="h-3 w-3" />
                        Country: {countryFilter}
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Warehouse Cards */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="border-t-4 border-t-orange-500">
                <CardHeader>
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <div className="flex gap-2">
                    <Skeleton className="h-5 w-16" />
                    <Skeleton className="h-5 w-16" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-2 w-full" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredWarehouses.length === 0 ? (
          <Card className="col-span-full border-dashed">
            <CardContent className="py-16 text-center">
              <Building2 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Warehouses Found</h3>
              <p className="text-gray-500 mb-6">
                {searchTerm || activeFilterCount > 0
                  ? 'Try adjusting your search or filters'
                  : 'Add a warehouse to start managing inventory'}
              </p>
              {user?.role === 'admin' && (
                <Button onClick={() => setIsAddDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Warehouse
                </Button>
              )}
            </CardContent>
          </Card>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredWarehouses.map((warehouse, index) => {
            const capacityPercentage = getCapacityPercentage(warehouse.currentStock, warehouse.capacity);
            return (
              <Card 
                key={warehouse.id} 
                className="hover:shadow-xl transition-all duration-300 border-t-4 border-t-orange-500 hover:scale-[1.02] group"
                style={{ animationDelay: `${index * 40}ms` }}
              >
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg break-words">{warehouse.name}</CardTitle>
                      <CardDescription className="flex items-center mt-2 gap-2 flex-wrap">
                        <Badge className={`${getTypeColor(warehouse.type)} whitespace-nowrap`}>
                          {warehouse.type}
                        </Badge>
                        <Badge className={`${getStatusColor(warehouse.status)} whitespace-nowrap`}>
                          {warehouse.status}
                        </Badge>
                        <span className="text-xs font-mono font-bold">{warehouse.code}</span>
                      </CardDescription>
                    </div>
                    <div className="flex space-x-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingWarehouse(warehouse);
                          setIsEditDialogOpen(true);
                        }}
                        title="Edit Warehouse"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleStatus(warehouse.code)}
                        title="Toggle Status"
                      >
                        {warehouse.status === 'Active' ? (
                          <ToggleRight className="h-4 w-4 text-green-600" />
                        ) : warehouse.status === 'Maintenance' ? (
                          <ToggleLeft className="h-4 w-4 text-yellow-600" />
                        ) : (
                          <ToggleLeft className="h-4 w-4 text-red-600" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteWarehouse(warehouse.code)}
                        title="Delete Warehouse"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-start gap-2 text-sm text-gray-600 min-w-0">
                      <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <div className="break-words">
                        {warehouse.address}<br />
                        {warehouse.city}, {warehouse.state} {warehouse.country}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600 min-w-0">
                      <Phone className="h-4 w-4 flex-shrink-0" />
                      <span className="break-all">{warehouse.phone}</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm text-gray-600 min-w-0">
                      <Users className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span className="break-words">Manager: {warehouse.manager}</span>
                    </div>

                    {/* Capacity Progress */}
                    <div className="pt-2">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">Capacity</span>
                        <span className={getCapacityColor(capacityPercentage)}>
                          {(warehouse.currentStock || 0).toLocaleString()} / {(warehouse.capacity || 0).toLocaleString()} ({capacityPercentage}%)
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            capacityPercentage >= 90 ? 'bg-red-500' :
                            capacityPercentage >= 70 ? 'bg-yellow-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${capacityPercentage}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="flex justify-between items-center pt-2 border-t">
                      <div className="flex items-center text-sm text-gray-500">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewTrips(warehouse)}
                          className="text-blue-600 hover:text-blue-800 mr-1"
                          title="View Trips"
                        >
                          <Package className="h-4 w-4" />
                        </Button>
                        <span>Trips: {(() => {
                          try {
                            const savedTrips = localStorage.getItem('shiphub-trips');
                            if (!savedTrips) return 0;

                            const tripsData = JSON.parse(savedTrips);
                            if (!Array.isArray(tripsData)) return 0;

                            return tripsData.filter((trip: any) =>
                              trip && typeof trip === 'object' &&
                              (
                                trip.originWarehouse === warehouse.name ||
                                trip.origin === warehouse.name ||
                                trip.destinationWarehouse === warehouse.name ||
                                trip.destination === warehouse.name
                              )
                            ).length;
                          } catch (e) {
                            console.error('Error calculating trip count:', e);
                            return 0;
                          }
                        })()} assigned</span>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-400">
                          Since {warehouse.createdAt}
                        </div>
                        <div className="flex space-x-1 mt-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadPDF(warehouse)}
                            className="text-green-600 hover:text-green-800"
                            title="Download Info"
                          >
                            Download
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePrint(warehouse)}
                            className="text-purple-600 hover:text-purple-800"
                            title="Print Info"
                          >
                            Print
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          </div>
        ) : (
          /* List View */
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Warehouse</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Capacity</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredWarehouses.map((warehouse) => {
                      const capacityPercentage = getCapacityPercentage(warehouse.currentStock, warehouse.capacity);
                      return (
                        <tr key={warehouse.id} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{warehouse.name}</div>
                            <div className="text-sm text-gray-500 font-mono">{warehouse.code}</div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm text-gray-900">{warehouse.city}, {warehouse.state}</div>
                            <div className="text-sm text-gray-500">{warehouse.country}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{warehouse.manager}</div>
                            <div className="text-sm text-gray-500">{warehouse.phone}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{warehouse.currentStock} / {warehouse.capacity}</div>
                            <div className="w-24 bg-gray-200 rounded-full h-1.5">
                              <div
                                className={`h-1.5 rounded-full ${
                                  capacityPercentage >= 90 ? 'bg-red-500' :
                                  capacityPercentage >= 70 ? 'bg-yellow-500' : 'bg-green-500'
                                }`}
                                style={{ width: `${capacityPercentage}%` }}
                              ></div>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <Badge className={getStatusColor(warehouse.status)}>
                              {warehouse.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end space-x-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewTrips(warehouse)}
                                title="View Trips"
                              >
                                <Truck className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditingWarehouse(warehouse);
                                  setIsEditDialogOpen(true);
                                }}
                                title="Edit"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleStatus(warehouse.code)}
                                title="Toggle Status"
                              >
                                {warehouse.status === 'Active' ? (
                                  <ToggleRight className="h-4 w-4 text-green-600" />
                                ) : (
                                  <ToggleLeft className="h-4 w-4 text-red-600" />
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Edit Warehouse Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Edit Warehouse</DialogTitle>
              <DialogDescription>
                Update warehouse information
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Warehouse Name</Label>
                  <Input
                    id="edit-name"
                    value={editingWarehouse?.name || ''}
                    onChange={(e) => setEditingWarehouse(prev => prev ? {...prev, name: e.target.value} : null)}
                    placeholder="Enter warehouse name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-location">Location</Label>
                  <Input
                    id="edit-location"
                    value={editingWarehouse?.location || ''}
                    onChange={(e) => setEditingWarehouse(prev => prev ? {...prev, location: e.target.value} : null)}
                    placeholder="City/Region"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-address">Address</Label>
                <Input
                  id="edit-address"
                  value={editingWarehouse?.address || ''}
                  onChange={(e) => setEditingWarehouse(prev => prev ? {...prev, address: e.target.value} : null)}
                  placeholder="Street address"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-city">City</Label>
                  <Input
                    id="edit-city"
                    value={editingWarehouse?.city || ''}
                    onChange={(e) => setEditingWarehouse(prev => prev ? {...prev, city: e.target.value} : null)}
                    placeholder="City"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-state">State</Label>
                  <Input
                    id="edit-state"
                    value={editingWarehouse?.state || ''}
                    onChange={(e) => setEditingWarehouse(prev => prev ? {...prev, state: e.target.value} : null)}
                    placeholder="State"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-country">Country</Label>
                  <Input
                    id="edit-country"
                    value={editingWarehouse?.country || ''}
                    onChange={(e) => setEditingWarehouse(prev => prev ? {...prev, country: e.target.value} : null)}
                    placeholder="Country"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-phone">Phone</Label>
                  <Input
                    id="edit-phone"
                    value={editingWarehouse?.phone || ''}
                    onChange={(e) => setEditingWarehouse(prev => prev ? {...prev, phone: e.target.value} : null)}
                    placeholder="Phone number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-manager">Manager</Label>
                  <Input
                    id="edit-manager"
                    value={editingWarehouse?.manager || ''}
                    onChange={(e) => setEditingWarehouse(prev => prev ? {...prev, manager: e.target.value} : null)}
                    placeholder="Manager name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-capacity">Capacity (units)</Label>
                  <Input
                    id="edit-capacity"
                    type="number"
                    value={editingWarehouse?.capacity || ''}
                    onChange={(e) => setEditingWarehouse(prev => prev ? {...prev, capacity: parseInt(e.target.value) || 0} : null)}
                    placeholder="Storage capacity"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-type">Type</Label>
                  <Select value={editingWarehouse?.type || ''} onValueChange={(value) => setEditingWarehouse(prev => prev ? {...prev, type: value as 'Main' | 'Branch' | 'Distribution'} : null)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select warehouse type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Main">Main</SelectItem>
                      <SelectItem value="Branch">Branch</SelectItem>
                      <SelectItem value="Distribution">Distribution</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleEditWarehouse}>Update Warehouse</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Trips Dialog */}
        <Dialog open={isTripsDialogOpen} onOpenChange={setIsTripsDialogOpen}>
          <DialogContent className="sm:max-w-[700px]">
            <DialogHeader>
              <DialogTitle>Trips at {selectedWarehouseName}</DialogTitle>
              <DialogDescription>
                Click on any trip to view its shipments
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[60vh] overflow-y-auto">
              {selectedWarehouseShipments.length > 0 ? (
                <div className="space-y-3">
                  {selectedWarehouseShipments.map((trip, index) => (
                    <div
                      key={index}
                      className="border rounded-lg p-4 hover:bg-blue-50 transition-colors cursor-pointer"
                      onClick={() => handleViewTripShipments(trip)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <Package className="h-4 w-4 text-blue-600" />
                            <span className="font-semibold">{trip.tripNumber}</span>
                            <Badge className={
                              trip.status === 'Completed' ? 'bg-green-100 text-green-800' :
                              trip.status === 'In Transit' ? 'bg-blue-100 text-blue-800' :
                              trip.status === 'Planning' ? 'bg-yellow-100 text-yellow-800' :
                              trip.status === 'At Destination' ? 'bg-purple-100 text-purple-800' :
                              'bg-gray-100 text-gray-800'
                            }>
                              {trip.status}
                            </Badge>
                            <Badge className={
                              trip.direction === 'Outgoing' ? 'bg-orange-100 text-orange-800' :
                              trip.direction === 'Incoming' ? 'bg-blue-100 text-blue-800' :
                              'bg-purple-100 text-purple-800'
                            }>
                              {trip.direction}
                            </Badge>
                          </div>
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Route:</span> {trip.origin} → {trip.destination}
                          </div>
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Driver:</span> {trip.driver}
                          </div>
                        </div>
                        <div className="text-right space-y-2">
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Vehicle:</span> {trip.vehicle || 'Unassigned'}
                          </div>
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Shipments:</span> {trip.shipments || 0}
                          </div>
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Weight:</span> {(trip.totalWeight || 0).toFixed(1)} kg
                          </div>
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Departure:</span> {trip.departureDate || 'Not set'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Trips Found</h3>
                  <p className="text-gray-500">This warehouse doesn't have any trips yet.</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={() => setIsTripsDialogOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Shipments Dialog */}
        <Dialog open={isShipmentsDialogOpen} onOpenChange={setIsShipmentsDialogOpen}>
          <DialogContent className="sm:max-w-[700px]">
            <DialogHeader>
              <DialogTitle>Shipments for {selectedTrip?.tripNumber || 'Trip'}</DialogTitle>
              <DialogDescription>
                View all shipments in this trip
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[60vh] overflow-y-auto">
              {selectedWarehouseShipments.length > 0 ? (
                <div className="space-y-3">
                  {selectedWarehouseShipments.map((shipment, index) => (
                    <div key={index} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex justify-between items-start">
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <Package className="h-4 w-4 text-blue-600" />
                            <span className="font-semibold">{shipment.shipmentNumber}</span>
                            <Badge className={
                              shipment.status === 'Delivered' ? 'bg-green-100 text-green-800' :
                              shipment.status === 'In Transit' ? 'bg-blue-100 text-blue-800' :
                              shipment.status === 'At Destination' ? 'bg-purple-100 text-purple-800' :
                              shipment.status === 'Processing' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }>
                              {shipment.status}
                            </Badge>
                          </div>
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Customer:</span> {shipment.customerName}
                          </div>
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Destination:</span> {shipment.destinationWarehouse}
                          </div>
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Created:</span> {shipment.createdAt}
                          </div>
                        </div>
                        <div className="text-right space-y-2">
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Weight:</span> {(shipment.weight || 0)} kg
                          </div>
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Volume:</span> {(shipment.volume || 0)} m³
                          </div>
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Packages:</span> {(shipment.packages || 0)}
                          </div>
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Est. Delivery:</span> {shipment.estimatedDelivery || 'Not set'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Shipments Found</h3>
                  <p className="text-gray-500">This warehouse doesn't have any shipments yet.</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={() => setIsShipmentsDialogOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}